import { LinkIcon, Clock, Ban, CheckCircle2, AlertTriangle } from "lucide-react"

// Full-screen message shown when the candidate's link can no longer be used, so they
// see a clear explanation instead of a raw error. `reason` comes from
// formAvailability; anything unrecognised falls back to the generic copy.
//
// `company` (name / contactEmail / contactPhone) is shown when we have it — the GET
// form endpoint returns it for an expired link. Mid-form failures don't carry it, so
// the block is optional.
const CONTENT = {
    invalid: {
        Icon: LinkIcon,
        tone: "amber",
        title: "This link is no longer active",
        description:
            "This verification link can no longer be used. If the request was re-sent, a newer link is waiting in your email — please open the most recent message and use the link there.",
    },
    expired: {
        Icon: Clock,
        tone: "amber",
        title: "This link has expired",
        description:
            "This verification link has expired. Please request a new one to continue.",
    },
    canceled: {
        Icon: Ban,
        tone: "red",
        title: "This verification was canceled",
        description:
            "This verification request has been canceled, so the form is no longer available.",
    },
    // The client made the Client Adjudication before the form was submitted.
    no_longer_required: {
        Icon: CheckCircle2,
        tone: "green",
        title: "No further action needed",
        description:
            "This verification is no longer required, so there's nothing more for you to do here.",
    },
    completed: {
        Icon: CheckCircle2,
        tone: "green",
        title: "This verification is already complete",
        description:
            "You've already submitted this verification — there's nothing more to do here.",
    },
    generic: {
        Icon: AlertTriangle,
        tone: "gray",
        title: "This form is unavailable",
        description:
            "This verification form can't be opened right now. Please use the most recent link from your email, or try again shortly.",
    },
}

const TONES = {
    amber: { bg: "bg-amber-100", fg: "text-amber-600" },
    red: { bg: "bg-red-100", fg: "text-red-600" },
    green: { bg: "bg-green-100", fg: "text-green-600" },
    gray: { bg: "bg-gray-100", fg: "text-gray-600" },
}

const FormUnavailable = ({ reason, company }) => {

    const content = CONTENT[reason] || CONTENT.generic
    const tone = TONES[content.tone] || TONES.gray
    const { Icon } = content

    const hasContact = company && (company.contactEmail || company.contactPhone)

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white shadow-xl rounded-2xl p-8 text-center">

                <div className="flex justify-center mb-6">
                    <div className={`w-16 h-16 flex items-center justify-center rounded-full ${tone.bg}`}>
                        <Icon className={`w-8 h-8 ${tone.fg}`} />
                    </div>
                </div>

                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                    {content.title}
                </h1>

                <p className="text-gray-600 mb-6">
                    {content.description}
                </p>

                <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-700 mb-6">
                    {hasContact ? (
                        <>
                            <p className="mb-1">
                                Need help? Contact {company.name || "the company that requested your verification"}:
                            </p>
                            {company.contactEmail && (
                                <p className="font-medium text-gray-800">{company.contactEmail}</p>
                            )}
                            {company.contactPhone && (
                                <p className="font-medium text-gray-800">{company.contactPhone}</p>
                            )}
                        </>
                    ) : (
                        <>
                            If you believe this is a mistake, please contact
                            {company?.name ? ` ${company.name}` : " the company that requested your verification"}.
                        </>
                    )}
                </div>

                <p className="text-xs text-gray-400">
                    This window can now be closed.
                </p>
            </div>
        </div>
    )
}

export default FormUnavailable
